import React from 'react';

/* ═══════════════════════════════════════════════════════════
   MobileStepWizard — full-screen, one-step-per-screen flow, mobile only
   (md:hidden). Desktop keeps whatever single-page layout the calling page
   already renders alongside this (wrapped in `hidden md:block` there) —
   this component doesn't touch desktop at all, it's a pure mobile overlay.

   Sits above the app chrome (navbar/bottom-nav are both fixed at z-50) so
   during the flow the user only sees this wizard's own back arrow +
   progress + step title, not the site nav — same "focused task" pattern
   real checkout/onboarding flows use.
═══════════════════════════════════════════════════════════ */
const MobileStepWizard = ({ steps, step, setStep, onSubmit, submitting, submitLabel = 'إرسال', error, onExit }) => {
  const total = steps.length;
  const current = steps[step];
  const isFirst = step === 0;
  const isLast  = step === total - 1;

  const goNext = () => {
    if (!current.isValid) return;
    if (isLast) { onSubmit(); return; }
    setStep((s) => Math.min(s + 1, total - 1));
  };

  const goBack = () => {
    if (isFirst) { onExit?.(); return; }
    setStep((s) => Math.max(s - 1, 0));
  };

  return (
    // bottom-16 (not inset-0) leaves room for the shared MobileBottomNav,
    // which stays mounted/visible underneath on every service page.
    <div className="md:hidden fixed inset-x-0 top-0 bottom-16 z-40 bg-[#F5F9FF] flex flex-col" dir="rtl">
      {/* ── Header: back + progress dots + step counter ── */}
      <div className="flex-shrink-0 bg-white border-b border-gray-100 px-4 pt-4 pb-3" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}>
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={goBack} className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 transition-colors flex-shrink-0">
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-6 bg-[#1E40AF]' : i < step ? 'w-1.5 bg-[#1E40AF]/50' : 'w-1.5 bg-gray-200'
              }`} />
            ))}
          </div>
          <span className="text-xs font-bold text-gray-400 w-9 flex-shrink-0 text-left">{step + 1}/{total}</span>
        </div>
        <h2 className="text-lg font-black text-[#1E3A8A]">{current.title}</h2>
        {current.subtitle && <p className="text-xs text-gray-500 mt-0.5">{current.subtitle}</p>}
      </div>

      {/* ── Step content — this one step's fields only; scrolls internally
           if it genuinely overflows a small screen, never the whole flow ── */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}
        {current.render()}
      </div>

      {/* ── Footer: single next/submit action ── */}
      <div className="flex-shrink-0 bg-white border-t border-gray-100 p-4" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
        <button
          type="button" onClick={goNext} disabled={!current.isValid || submitting}
          className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-black py-3.5 rounded-xl transition-all"
        >
          {submitting ? '...جارٍ الإرسال' : isLast ? submitLabel : 'التالي'}
        </button>
      </div>
    </div>
  );
};

export default MobileStepWizard;
